/**
 * LSP-19543: a tab-wide, in-memory record of which upload sessions the background durable drain is
 * currently driving.
 *
 * The green "Files were uploaded successfully." toast is a claim about every upload in this tab, not
 * about the batch that happened to finish last. PR #4747 made the foreground dialog reference-count
 * its own batches so only the batch taking the count to zero speaks — but the drain resumes rows in
 * the background after a reload, and neither side could see the other. A foreground batch finishing
 * while the drain was still resuming reloaded rows therefore announced that everything was done.
 *
 * This module is the missing signal: the drain brackets each resume attempt with
 * `beginBackgroundUploadAttempt` / `endBackgroundUploadAttempt`, and the dialog's toast gate reads
 * `backgroundUploadAttemptsInFlight()`. Session ids rather than a bare counter so a double `end`
 * (a `finally` plus a defensive caller) cannot drive the count negative or below the real work.
 */
const backgroundUploadSessionIdsInFlight = new Set<string>();

/** Records that the background drain has started driving `sessionId`. Idempotent. */
export function beginBackgroundUploadAttempt(sessionId: string): void {
	backgroundUploadSessionIdsInFlight.add(sessionId);
}

/** Records that the background drain's attempt on `sessionId` has ended, however it ended. Idempotent. */
export function endBackgroundUploadAttempt(sessionId: string): void {
	backgroundUploadSessionIdsInFlight.delete(sessionId);
}

/** How many sessions the background drain is driving right now. Never negative. */
export function backgroundUploadAttemptsInFlight(): number {
	return backgroundUploadSessionIdsInFlight.size;
}

/** Test-only: clears the shared module state between cases. */
export function resetUploadActivityForTests(): void {
	backgroundUploadSessionIdsInFlight.clear();
}
